/**
 * AG Post Editor, frontend
 * Lightweight router: login → list/new → editor (create or edit).
 * Reads config from window.AG_POST_EDITOR_CONFIG (endpoint, rootSelector).
 */
(function () {
  'use strict';

  // ── Config & state ───────────────────────────────────────
  const cfg = window.AG_POST_EDITOR_CONFIG || {};
  const endpoint = cfg.endpoint || '';
  const bridgeLabel = cfg.bridgeLabel || '';   // e.g. "Push to Stealth EU", blank disables the bridge button
  const bridgeTargetShort = /\bUK\b/i.test(bridgeLabel) ? 'UK' : 'EU';  // short tag for the "On EU/UK" badge
  const root = document.querySelector(cfg.rootSelector || '[data-ag-pe-root]');
  if (!root) return;

  const state = {
    password: sessionStorage.getItem('agpe_password') || '',
    view: 'login',           // login | list | editor
    blogs: [],
    articles: [],
    currentArticle: null,    // null = create; otherwise the article being edited
    quill: null,
    selectedProducts: [],    // [{id, title, imageUrl}]
    featuredImage: { url: '', altText: '' },
    authorAvatarUrl: '',
    bridgeSelection: new Set(),  // article ids ticked for batch "Push to partner store"
  };

  // ── API helper ───────────────────────────────────────────
  async function api(action, payload, timeoutMs) {
    // With the OTP shell present (AG-Admin-Shell), route through the auth
    // Worker's gate+proxy: it checks the caller's OTP session + 'post-editor'
    // section and injects the staff password server-side. The browser never
    // holds a password. Falls back to the legacy direct /exec call otherwise.
    if (window.AG_ADMIN && typeof window.AG_ADMIN.api === 'function') {
      return window.AG_ADMIN.api(action, payload || {});
    }
    if (!endpoint) throw new Error('No endpoint configured.');
    const body = Object.assign({ action: action, password: state.password }, payload || {});
    const controller = new AbortController();
    const t = timeoutMs || 45000;
    const timer = setTimeout(() => controller.abort(), t);
    let resp;
    try {
      resp = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify(body),
        signal: controller.signal
      });
    } catch (e) {
      clearTimeout(timer);
      if (e.name === 'AbortError') throw new Error('Request timed out after ' + (t / 1000) + 's. Apps Script may be cold. Try again.');
      throw e;
    }
    clearTimeout(timer);
    let data;
    try { data = await resp.json(); } catch (e) { data = { error: 'bad_response' }; }
    if (!resp.ok || data.error) {
      const msg = data.error === 'auth' ? 'Wrong password' : (data.error || 'Request failed');
      throw new Error(msg);
    }
    return data;
  }

  // ── Toasts ───────────────────────────────────────────────
  function toast(msg, kind) {
    const el = document.createElement('div');
    el.className = 'ag-pe-toast' + (kind ? ' ag-pe-toast--' + kind : '');
    // Errors get a richer card: heading + body + dismiss button. Other
    // kinds (success / info) stay as the simple single-line slide-in.
    if (kind === 'error') {
      el.classList.add('ag-pe-toast--rich');
      const safe = String(msg).replace(/[&<>"]/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;'}[c]));
      el.innerHTML = `
        <div class="ag-pe-toast__head">
          <strong>Couldn't save</strong>
          <button type="button" class="ag-pe-toast__close" aria-label="Dismiss">×</button>
        </div>
        <p class="ag-pe-toast__body">${safe}</p>
      `;
      el.querySelector('.ag-pe-toast__close').addEventListener('click', () => {
        el.classList.remove('is-visible');
        setTimeout(() => el.remove(), 300);
      });
    } else {
      el.textContent = msg;
    }
    document.body.appendChild(el);
    requestAnimationFrame(() => el.classList.add('is-visible'));
    // Errors stay until dismissed; success/info auto-hide.
    if (kind !== 'error') {
      setTimeout(() => {
        el.classList.remove('is-visible');
        setTimeout(() => el.remove(), 300);
      }, 3200);
    }
  }

  // Turn raw backend / network errors into something the editor user
  // can actually act on. Falls back to the raw message if nothing
  // matches so we never swallow real diagnostic info.
  function friendlyError(err) {
    const raw = (err && err.message) ? String(err.message) : String(err || 'Unknown error');

    // GraphQL errors come back as "GraphQL errors: [{...}]", parse out
    // the message + field path so we can map them to plain English.
    if (raw.indexOf('GraphQL errors:') === 0) {
      let arr;
      try { arr = JSON.parse(raw.slice('GraphQL errors:'.length).trim()); } catch (e) { arr = null; }
      if (Array.isArray(arr) && arr.length) {
        const first = arr[0] || {};
        const path = ((first.extensions && first.extensions.value && Object.keys(first.extensions.value)) || []);
        const errPath = (first.problems && first.problems[0] && first.problems[0].path && first.problems[0].path.join('.')) ||
                        (first.path && first.path.join && first.path.join('.')) ||
                        (first.problems && first.problems[0] && first.problems[0].path) ||
                        '';
        const fieldMap = {
          'author': 'Author name',
          'title': 'Post title',
          'body': 'Post body',
          'handle': 'URL handle',
          'blogId': 'Blog',
          'summary': 'Summary',
          'publishedAt': 'Publish date',
          'image': 'Featured image',
          'tags': 'Tags'
        };
        const friendlyField = fieldMap[errPath] || errPath;
        const m = String(first.message || '');
        if (m.indexOf('Expected value to not be null') !== -1) {
          return `"${friendlyField || 'A required field'}" can't be empty. Fill it in and try again.`;
        }
        if (m.indexOf('is invalid') !== -1) {
          return `"${friendlyField || 'A field'}" has an invalid value. Check the format and try again.`;
        }
        if (m.indexOf('too long') !== -1) {
          return `"${friendlyField || 'A field'}" is too long. Trim it down and try again.`;
        }
        if (m.indexOf('has already been taken') !== -1) {
          return 'A post with this URL handle already exists. Pick a different handle.';
        }
        // Fallback: clean the raw GraphQL noise off the front
        return friendlyField
          ? `Backend rejected "${friendlyField}": ${m}`
          : m;
      }
    }

    // Shopify userErrors come through as JSON-stringified arrays
    if (raw.indexOf('userErrors') !== -1 || raw.startsWith('[{')) {
      try {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr) && arr[0] && arr[0].message) {
          const f = (arr[0].field && arr[0].field.join && arr[0].field.join('.')) || '';
          return f ? `${arr[0].message} (${f})` : arr[0].message;
        }
      } catch (e) {}
    }

    // Network-y messages
    if (/Failed to fetch|NetworkError|net::|ERR_INTERNET_DISCONNECTED/i.test(raw)) {
      return 'Lost connection to the editor backend. Check your internet, then try again.';
    }
    if (/timed? ?out|AbortError/i.test(raw)) {
      return 'The request timed out. Shopify may be slow, wait a moment and try again.';
    }
    if (/401|auth|unauthori[sz]ed/i.test(raw)) {
      return 'Your editor session has expired. Reload the page and log in again.';
    }
    if (/405/.test(raw)) {
      return 'Backend rejected the request (405). The Apps Script web app may need re-deploying.';
    }

    // Last resort, short, generic
    return raw.length > 200 ? raw.slice(0, 200) + '…' : raw;
  }

  // ── Login view ───────────────────────────────────────────
  function renderLogin(errorMsg) {
    root.innerHTML = `
      <div class="ag-pe-login">
        <h2 class="ag-pe-login__title">Staff Login</h2>
        <form class="ag-pe-login__form" id="ag-pe-login-form">
          <input type="password" class="ag-pe-input" id="ag-pe-pw" placeholder="Password" required autofocus>
          <button type="submit" class="ag-pe-btn">Unlock</button>
          ${errorMsg ? `<p class="ag-pe-login__error">${escapeHtml(errorMsg)}</p>` : ''}
        </form>
      </div>
    `;
    document.getElementById('ag-pe-login-form').addEventListener('submit', async (e) => {
      e.preventDefault();
      state.password = document.getElementById('ag-pe-pw').value;
      try {
        // smoke test the password by listing blogs
        const data = await api('list_blogs');
        state.blogs = data.blogs || [];
        sessionStorage.setItem('agpe_password', state.password);
        await goList();
      } catch (err) {
        state.password = '';
        sessionStorage.removeItem('agpe_password');
        renderLogin(err.message);
      }
    });
  }

  // ── List view ────────────────────────────────────────────
  async function goList() {
    state.view = 'list';
    state.bridgeSelection.clear();
    root.innerHTML = `
      <div class="ag-pe-tabs">
        <button class="ag-pe-tab is-active" data-tab="list">Posts</button>
        <button class="ag-pe-tab" data-tab="new">+ New post</button>
        <button class="ag-pe-tab" data-tab="info">How it works</button>
      </div>
      <div class="ag-pe-list__filter">
        <select class="ag-pe-select" id="ag-pe-list-blog">
          <option value="">All blogs</option>
          ${state.blogs.map(b => `<option value="${escapeAttr(b.id)}">${escapeHtml(b.title)}</option>`).join('')}
        </select>
        <input type="search" class="ag-pe-input" id="ag-pe-list-search" placeholder="Search title…">
      </div>
      <div id="ag-pe-list-results">
        <div class="ag-pe-spinner-block"><span class="ag-pe-loading"></span>Loading posts…</div>
      </div>
    `;
    bindTabs();

    let debounce;
    document.getElementById('ag-pe-list-blog').addEventListener('change', loadList);
    document.getElementById('ag-pe-list-search').addEventListener('input', () => {
      clearTimeout(debounce);
      debounce = setTimeout(loadList, 250);
    });
    await loadList();
  }

  async function loadList() {
    const blogId = document.getElementById('ag-pe-list-blog').value;
    const query  = document.getElementById('ag-pe-list-search').value;
    const target = document.getElementById('ag-pe-list-results');
    target.innerHTML = `<div class="ag-pe-spinner-block"><span class="ag-pe-loading"></span>Loading…</div>`;
    try {
      const data = await api('list_articles', { blog_id: blogId, query: query });
      state.articles = data.articles || [];
      renderListResults(target);
    } catch (err) {
      target.innerHTML = `<div class="ag-pe-error-banner">${escapeHtml(err.message)}</div>`;
    }
  }

  function renderListResults(target) {
    if (!state.articles.length) {
      target.innerHTML = `<div class="ag-pe-card"><p style="margin:0;color:var(--pe-muted)">No posts yet.</p></div>`;
      return;
    }
    const canBridge = !!bridgeLabel;
    const bar = canBridge ? `
      <div class="ag-pe-batch-bar">
        <label class="ag-pe-batch-all"><input type="checkbox" id="ag-pe-select-all"> Select all</label>
        <span class="ag-pe-batch-count" id="ag-pe-batch-count">0 selected</span>
        <button type="button" class="ag-pe-btn ag-pe-btn--ghost ag-pe-btn--bridge ag-pe-btn--small" id="ag-pe-batch-push" disabled>${escapeHtml(bridgeLabel)}</button>
      </div>` : '';
    target.innerHTML = bar + `<div class="ag-pe-list">${state.articles.map(a => `
      <div class="ag-pe-list-item" data-article-id="${escapeAttr(a.id)}">
        ${canBridge ? `<input type="checkbox" class="ag-pe-list-item__check" data-article-id="${escapeAttr(a.id)}" aria-label="Select for ${escapeAttr(bridgeLabel)}">` : ''}
        <img class="ag-pe-list-item__thumb" src="${escapeAttr(a.imageUrl || '')}" alt="" onerror="this.style.visibility='hidden'">
        <div class="ag-pe-list-item__body">
          <h3 class="ag-pe-list-item__title">${escapeHtml(a.title)}</h3>
          <div class="ag-pe-list-item__meta">
            ${(() => {
              const s = articleStatus(a);
              return `<span class="ag-pe-list-item__status ag-pe-list-item__status--${s}">${articleStatusLabel(s)}</span>`;
            })()}
            <span>${escapeHtml(a.blogTitle || '')}</span>
            <span>${formatDate(a.publishedAt)}</span>
            ${a.authorName ? `<span>by ${escapeHtml(a.authorName)}</span>` : ''}
            ${a.bridged ? `<span class="ag-pe-list-item__bridged" title="Sent to ${escapeAttr(a.bridgedStore || bridgeLabel || 'partner store')}${a.bridgedAt ? ' · ' + escapeAttr(formatDate(a.bridgedAt)) : ''}">↔ On ${escapeHtml(bridgeTargetShort)}</span>` : ''}
          </div>
        </div>
      </div>
    `).join('')}</div>`;

    // Row click opens the editor, but not when the checkbox itself is clicked.
    target.querySelectorAll('.ag-pe-list-item').forEach(el => {
      el.addEventListener('click', (e) => {
        if (e.target.closest('.ag-pe-list-item__check')) return;
        goEditor(el.getAttribute('data-article-id'));
      });
    });

    if (canBridge) wireBatchBridge(target);
  }

  // ── Batch "Push to partner store" ────────────────────────
  function wireBatchBridge(target) {
    const countEl   = target.querySelector('#ag-pe-batch-count');
    const pushBtn   = target.querySelector('#ag-pe-batch-push');
    const selectAll = target.querySelector('#ag-pe-select-all');
    const checks    = Array.from(target.querySelectorAll('.ag-pe-list-item__check'));
    const sel       = state.bridgeSelection;

    function sync() {
      countEl.textContent = sel.size + ' selected';
      pushBtn.disabled = sel.size === 0;
      if (selectAll) selectAll.checked = checks.length > 0 && sel.size === checks.length;
    }
    // Restore selection (e.g. after a re-render) onto the checkboxes.
    checks.forEach(c => { c.checked = sel.has(c.getAttribute('data-article-id')); });
    sync();

    checks.forEach(c => {
      c.addEventListener('click', e => e.stopPropagation()); // never open the editor
      c.addEventListener('change', () => {
        const id = c.getAttribute('data-article-id');
        if (c.checked) sel.add(id); else sel.delete(id);
        sync();
      });
    });
    if (selectAll) {
      selectAll.addEventListener('change', () => {
        checks.forEach(c => {
          c.checked = selectAll.checked;
          const id = c.getAttribute('data-article-id');
          if (selectAll.checked) sel.add(id); else sel.delete(id);
        });
        sync();
      });
    }
    pushBtn.addEventListener('click', () => pushSelectedToBridge(target));
  }

  async function pushSelectedToBridge(target) {
    const ids = Array.from(state.bridgeSelection);
    if (!ids.length) return;
    const ok = confirm(
      'Send ' + ids.length + ' post' + (ids.length > 1 ? 's' : '') + ' to ' + bridgeLabel + '?\n\n' +
      'Each is machine-translated and created as a DRAFT in the other store for review. ' +
      'Nothing is published. Related products are not carried over; links may need re-pointing.'
    );
    if (!ok) return;

    const pushBtn = target.querySelector('#ag-pe-batch-push');
    let done = 0, failed = 0;
    pushBtn.disabled = true;
    // Sequential. Apps Script + the partner translate call are heavy; a
    // parallel burst risks timeouts and rate limits.
    for (let i = 0; i < ids.length; i++) {
      pushBtn.innerHTML = `<span class="ag-pe-loading"></span>Sending ${i + 1}/${ids.length}…`;
      try {
        const r = await api('bridge_push', { article_id: ids[i] }, 180000);
        done++;
        const art = state.articles.find(a => a.id === ids[i]);   // optimistic badge
        if (art) { art.bridged = true; if (r && r.targetStore) art.bridgedStore = r.targetStore; }
      } catch (err) {
        failed++;
      }
    }
    state.bridgeSelection.clear();
    toast(`Sent ${done} to ${bridgeTargetShort}` + (failed ? ` · ${failed} failed` : '') + '.', failed ? 'error' : 'success');
    renderListResults(target);   // refresh badges + clear ticks
  }

  // ── Editor view ──────────────────────────────────────────
  async function goEditor(articleId) {
    state.view = 'editor';
    state.selectedProducts = [];
    state.featuredImage = { url: '', altText: '' };

    if (articleId) {
      root.innerHTML = `<div class="ag-pe-spinner-block"><span class="ag-pe-loading"></span>Loading article…</div>`;
      try {
        const data = await api('get_article', { article_id: articleId });
        state.currentArticle = data.article;
      } catch (err) {
        root.innerHTML = `<div class="ag-pe-error-banner">${escapeHtml(err.message)}</div>`;
        return;
      }
    } else {
      state.currentArticle = null;
    }

    renderEditor();
    initQuill();

    if (state.currentArticle) {
      hydrateForm(state.currentArticle);
    }
  }

  function renderEditor() {
    const isEdit = !!state.currentArticle;
    root.innerHTML = `
      <div class="ag-pe-tabs">
        <button class="ag-pe-tab" data-tab="list">← All posts</button>
        <button class="ag-pe-tab is-active" data-tab="editor">${isEdit ? 'Edit post' : 'New post'}</button>
      </div>

      <div class="ag-pe-grid">
        <div>
          <div class="ag-pe-card">
            <h3 class="ag-pe-card__title">Title & body</h3>
            <div class="ag-pe-field">
              <label class="ag-pe-label" for="ag-pe-title">Title</label>
              <input id="ag-pe-title" class="ag-pe-input" required maxlength="160">
            </div>
            <div class="ag-pe-field">
              <label class="ag-pe-label" for="ag-pe-summary">Excerpt / summary <small>1–2 sentences shown at the top of the article and on listing cards</small></label>
              <textarea id="ag-pe-summary" class="ag-pe-textarea" rows="2" maxlength="500"></textarea>
            </div>
            <div class="ag-pe-field" id="ag-pe-body-field">
              <div class="ag-pe-body-head">
                <label class="ag-pe-label" style="margin:0">Body</label>
                <div class="ag-pe-body-head__actions">
                  <button type="button" class="ag-pe-btn ag-pe-btn--ghost ag-pe-btn--small ag-pe-fs-only" id="ag-pe-fs-save-draft">Save as draft</button>
                  <button type="button" class="ag-pe-btn ag-pe-btn--small ag-pe-fs-only" id="ag-pe-fs-save">${isEdit ? 'Save changes' : 'Create post'}</button>
                  <button type="button" class="ag-pe-btn ag-pe-btn--ghost ag-pe-btn--small" id="ag-pe-fullscreen" title="Expand the editor to fill the screen (Esc to close)">⛶ Expand</button>
                </div>
              </div>
              <div class="ag-pe-quicklinks ag-pe-fs-only" id="ag-pe-quicklinks" data-ag-pe-quicklinks></div>
              <div class="ag-pe-editor" id="ag-pe-editor"></div>
            </div>
          </div>

          <div class="ag-pe-card">
            <h3 class="ag-pe-card__title">Key takeaways <small>3–5 short bullets, one per line. Appears at the top of the article + drives the AI search snippet.</small></h3>
            <textarea id="ag-pe-takeaways" class="ag-pe-textarea" rows="4" placeholder="Adjustable dumbbells save space&#10;A single pair scales from 5 to 50kg&#10;Five minutes a month of maintenance keeps them combat-ready"></textarea>
          </div>


          <div class="ag-pe-card">
            <h3 class="ag-pe-card__title">Search engine listing (SEO)</h3>
            <div class="ag-pe-field">
              <label class="ag-pe-label" for="ag-pe-post-type">Post type <small>Drives the structured data Google + AI assistants see</small></label>
              <select id="ag-pe-post-type" class="ag-pe-select">
                <option value="general">General</option>
                <option value="buyer-guide">Buyer Guide</option>
                <option value="how-to">How-To (H2s become numbered steps in Google)</option>
                <option value="faq">FAQ (H2 = question, next paragraph = answer)</option>
              </select>
            </div>
            <div class="ag-pe-field">
              <label class="ag-pe-label" for="ag-pe-seo-title">Meta title <small>50–60 characters works best</small></label>
              <input id="ag-pe-seo-title" class="ag-pe-input" maxlength="70">
            </div>
            <div class="ag-pe-field">
              <label class="ag-pe-label" for="ag-pe-seo-desc">Meta description <small>150–160 characters works best</small></label>
              <textarea id="ag-pe-seo-desc" class="ag-pe-textarea" rows="2" maxlength="320"></textarea>
            </div>
            <div class="ag-pe-field">
              <label class="ag-pe-label" for="ag-pe-handle">URL slug <small>Leave blank to auto-generate from title</small></label>
              <input id="ag-pe-handle" class="ag-pe-input" placeholder="post-url-slug">
            </div>
          </div>

          <div class="ag-pe-card">
            <h3 class="ag-pe-card__title">Author bio <small>Boosts E-E-A-T signals on training / equipment content</small></h3>
            <div class="ag-pe-field">
              <label class="ag-pe-label" for="ag-pe-author-bio">Bio</label>
              <textarea id="ag-pe-author-bio" class="ag-pe-textarea" rows="3" placeholder="Short bio: role, credentials, years of experience."></textarea>
            </div>
            <div class="ag-pe-field">
              <label class="ag-pe-label">Author avatar</label>
              <div class="ag-pe-image" id="ag-pe-avatar-wrap">
                <img class="ag-pe-image__preview" id="ag-pe-avatar-preview" style="display:none">
                <div class="ag-pe-avatar-actions">
                  <button type="button" class="ag-pe-btn ag-pe-btn--ghost ag-pe-btn--small" id="ag-pe-avatar-pick">Upload avatar</button>
                  <button type="button" class="ag-pe-btn ag-pe-btn--ghost ag-pe-btn--small" id="ag-pe-avatar-choose">Choose previous…</button>
                </div>
                <input type="file" id="ag-pe-avatar-input" accept="image/*">
              </div>
            </div>
            <div class="ag-pe-field">
              <label class="ag-pe-label" for="ag-pe-author-linkedin">LinkedIn URL</label>
              <input id="ag-pe-author-linkedin" class="ag-pe-input" placeholder="https://www.linkedin.com/in/…">
            </div>
          </div>
        </div>

        <aside>
          <div class="ag-pe-card">
            <h3 class="ag-pe-card__title">Status</h3>
            <div class="ag-pe-field">
              <label class="ag-pe-label" for="ag-pe-status">Visibility</label>
              <select id="ag-pe-status" class="ag-pe-select">
                <option value="draft">Draft (hidden)</option>
                <option value="publish">Publish now (visible)</option>
                <option value="schedule">Schedule for later</option>
              </select>
            </div>
            <div class="ag-pe-field" id="ag-pe-schedule-wrap" style="display:none">
              <label class="ag-pe-label" for="ag-pe-schedule">Publish on <small>your local time</small></label>
              <input type="datetime-local" id="ag-pe-schedule" class="ag-pe-input">
              <p class="ag-pe-help">Article stays hidden until this date/time, then Shopify makes it live automatically.</p>
            </div>
          </div>

          <div class="ag-pe-card">
            <h3 class="ag-pe-card__title">Featured image</h3>
            <div class="ag-pe-image" id="ag-pe-image-wrap">
              <img class="ag-pe-image__preview" id="ag-pe-image-preview" style="display:none">
              <p class="ag-pe-image__hint" id="ag-pe-image-hint">JPG/PNG/WebP, ideally 1600×1200+.</p>
              <button type="button" class="ag-pe-btn ag-pe-btn--ghost ag-pe-btn--small" id="ag-pe-image-pick">Upload image</button>
              <button type="button" class="ag-pe-btn ag-pe-btn--ghost ag-pe-btn--small" id="ag-pe-image-clear" style="display:none">Remove</button>
              <input type="file" id="ag-pe-image-input" accept="image/*">
            </div>
            <div class="ag-pe-field" style="margin-top:10px">
              <label class="ag-pe-label" for="ag-pe-image-alt">Image alt text <small>describes the image for SEO + accessibility</small></label>
              <input id="ag-pe-image-alt" class="ag-pe-input">
            </div>
          </div>

          <div class="ag-pe-card">
            <h3 class="ag-pe-card__title">Organisation</h3>
            <div class="ag-pe-field">
              <label class="ag-pe-label" for="ag-pe-blog">Blog</label>
              <select id="ag-pe-blog" class="ag-pe-select" required ${isEdit ? 'disabled' : ''}>
                <option value="">Pick a blog…</option>
                ${state.blogs.map(b => `<option value="${escapeAttr(b.id)}">${escapeHtml(b.title)}</option>`).join('')}
              </select>
              ${isEdit ? `<p class="ag-pe-help">Existing posts can't change blog. Delete + recreate to move.</p>` : ''}
            </div>
            <div class="ag-pe-field">
              <label class="ag-pe-label" for="ag-pe-topic">Topic <small>becomes a filter pill on the blog</small></label>
              <select id="ag-pe-topic" class="ag-pe-select">
                <option value="">No topic</option>
                ${renderTopicOptions()}
                <option value="__new__">+ New topic…</option>
              </select>
              <input id="ag-pe-topic-new" class="ag-pe-input" placeholder="New topic name" style="display:none;margin-top:6px">
              <p class="ag-pe-help">Adds a <code>topic:</code> tag. Only these show as blog filter pills. Your other tags below stay for SEO.</p>
            </div>
            <div class="ag-pe-field">
              <label class="ag-pe-label" for="ag-pe-author">Author</label>
              <input id="ag-pe-author" class="ag-pe-input">
            </div>
            <div class="ag-pe-field">
              <label class="ag-pe-label">Tags <small>type and press enter or comma to add</small></label>
              <div class="ag-pe-chips" id="ag-pe-tags">
                <input type="text" class="ag-pe-chips__input" id="ag-pe-tags-input" placeholder="Add a tag…">
              </div>
            </div>
          </div>

          <div class="ag-pe-card">
            <h3 class="ag-pe-card__title">Related products <small>shown in the article body</small></h3>
            <div class="ag-pe-prod-picker">
              <input type="search" class="ag-pe-input" id="ag-pe-prod-search" placeholder="Search products…">
              <div class="ag-pe-prod-results" id="ag-pe-prod-results" style="display:none"></div>
            </div>
            <div class="ag-pe-prod-selected" id="ag-pe-prod-selected"></div>
          </div>

          <div class="ag-pe-card ag-pe-checklist-card">
            <h3 class="ag-pe-card__title">SEO checklist</h3>
            <ul class="ag-pe-checklist" id="ag-pe-checklist"></ul>
          </div>
        </aside>
      </div>

      <div class="ag-pe-btn-row">
        <button class="ag-pe-btn" id="ag-pe-save">${isEdit ? 'Save changes' : 'Create post'}</button>
        <button class="ag-pe-btn ag-pe-btn--ghost" id="ag-pe-save-draft" title="Save without publishing. Flips the post to Draft regardless of the Visibility setting above">Save as draft</button>
        <button class="ag-pe-btn ag-pe-btn--ghost" id="ag-pe-cancel">Cancel</button>
        ${isEdit && state.currentArticle ? (() => {
          const a = state.currentArticle;
          const idNum = String(a.id || '').split('/').pop();
          const adminUrl = `https://assassin-goods2.myshopify.com/admin/articles/${escapeAttr(idNum)}`;
          // Public URL only shown for live posts with valid handles,
          // and even then, can 404 if Shopify auto-modified the handle
          // on save or the publishedAt is still in the future. The
          // admin button is the reliable fallback (it always works
          // and has Shopify's own Preview button inside).
          let html = '';
          if (a.isPublished && a.blogHandle && a.handle) {
            const url = `/blogs/${escapeAttr(a.blogHandle)}/${escapeAttr(a.handle)}`;
            html += `<a class="ag-pe-btn ag-pe-btn--ghost" href="${url}" target="_blank" title="${escapeAttr(url)}">Preview live ↗</a>`;
          }
          html += `<a class="ag-pe-btn ag-pe-btn--ghost" href="${adminUrl}" target="_blank" title="Always works. Shopify admin's article page has its own Preview button for drafts + scheduled posts">Open in admin ↗</a>`;
          return html;
        })() : ''}
        ${isEdit && state.currentArticle ? `
          <div class="ag-pe-move-wrap">
            <select id="ag-pe-move-target" class="ag-pe-select">
              <option value="">Move to blog…</option>
              ${state.blogs.filter(b => b.id !== state.currentArticle.blogId).map(b => `<option value="${escapeAttr(b.id)}">${escapeHtml(b.title)}</option>`).join('')}
            </select>
            <button type="button" class="ag-pe-btn ag-pe-btn--ghost ag-pe-btn--danger" id="ag-pe-move">Move</button>
          </div>
        ` : ''}
        ${isEdit && state.currentArticle && bridgeLabel ? `
          <button type="button" class="ag-pe-btn ag-pe-btn--ghost ag-pe-btn--bridge" id="ag-pe-bridge" title="Machine-translate this post and create it as a DRAFT in the partner store for review. Related products aren't carried over and links may need re-pointing.">${escapeHtml(bridgeLabel)}</button>
        ` : ''}
      </div>
    `;
    bindTabs();
    bindEditorEvents();
  }

  function initQuill() {
    if (!window.Quill) {
      // Quill not yet loaded. Retry shortly
      setTimeout(initQuill, 150);
      return;
    }
    const target = document.getElementById('ag-pe-editor');
    if (!target) return;

    // Register quill-table-up if its UMD bundle loaded. The package exposes
    // its main class as window.TableUp.default and helpers as named exports
    // on the same object (defaultCustomSelect, TableMenuContextmenu, etc.).
    let TU = null;
    if (window.TableUp) {
      TU = window.TableUp.default || window.TableUp;
      try {
        Quill.register({ ['modules/' + TU.moduleName]: TU }, true);
      } catch (e) {
        TU = null;
      }
    }

    const toolbarContainer = [
      [{ header: [2, 3, 4, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['blockquote', 'code-block'],
      ['link', 'image', 'ag-product-link', 'ag-collection-link'],
      [{ align: [] }],
      [{ color: [] }]
    ];
    if (TU) toolbarContainer.push([{ [TU.toolName]: [] }]);
    toolbarContainer.push(['clean']);

    const modules = {
      toolbar: {
        container: toolbarContainer,
        handlers: {
          image: handleEditorImage,
          'ag-product-link': () => openLinkPicker('product'),
          'ag-collection-link': () => openLinkPicker('collection')
        }
      }
    };
    if (TU) {
      const tu = window.TableUp;
      modules[TU.moduleName] = {
        full: true,
        customSelect: tu.defaultCustomSelect,
        customBtn: true,
        texts: { customBtnText: 'Custom size', confirmText: 'Insert', cancelText: 'Cancel' },
        modules: [
          tu.TableResizeBox && { module: tu.TableResizeBox },
          tu.TableResizeLine && { module: tu.TableResizeLine },
          tu.TableSelection && { module: tu.TableSelection },
          tu.TableMenuContextmenu && { module: tu.TableMenuContextmenu },
          tu.TableAlign && { module: tu.TableAlign }
        ].filter(Boolean)
      };
    }

    state.quill = new Quill(target, { theme: 'snow', modules: modules, placeholder: 'Write your post…' });

    if (state.currentArticle && state.currentArticle.body) {
      state.quill.clipboard.dangerouslyPasteHTML(state.currentArticle.body);
    }

    // Track the last known cursor position. Clicking a sidebar/header
    // button (e.g. a Quick-link chip) steals focus from Quill, which
    // makes Quill.getSelection() return null at click-time. And the
    // insert lands at the end of the document. Listening to
    // selection-change lets us remember where the cursor WAS just
    // before the user clicked the chip, and insert there instead.
    state.lastRange = { index: state.quill.getLength(), length: 0 };
    state.quill.on('selection-change', function (range) {
      if (range) state.lastRange = range;
    });
  }

  // ── Fullscreen body editor ────────────────────────────────────
  // Toggle the body field into a viewport-filling overlay. The
  // overlay has its own scroll context, so the Quill toolbar's
  // CSS position:sticky actually works (the theme's ancestor
  // overflow that was breaking sticky doesn't apply inside the
  // fixed element). Escape key + the close button both exit.
  function toggleEditorFullscreen() {
    const field = document.getElementById('ag-pe-body-field');
    if (!field) return;
    const becoming = !field.classList.contains('is-fullscreen');
    field.classList.toggle('is-fullscreen', becoming);
    document.documentElement.classList.toggle('ag-pe-scroll-lock', becoming);
    const btn = document.getElementById('ag-pe-fullscreen');
    if (btn) btn.innerHTML = becoming ? '✕ Close' : '⛶ Expand';
    if (becoming && state.quill) state.quill.focus();
  }

  function handleEditorImage() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.click();
    input.onchange = async () => {
      const file = input.files && input.files[0];
      if (!file) return;
      const altText = (prompt('Alt text for this image (briefly describe what it shows: required for SEO + accessibility):', '') || '').trim();
      try {
        toast('Uploading image…');
        const prepared = await prepareImage(file);
        const base64 = await readFileAsBase64(prepared);
        const result = await api('upload_image', {
          filename: file.name,
          mimeType: prepared.type || file.type,
          base64: base64,
          altText: altText
        });
        const range = state.quill.getSelection(true);
        state.quill.insertEmbed(range.index, 'image', result.url, 'user');
        // Add alt attribute to the freshly inserted image
        if (altText) {
          requestAnimationFrame(() => {
            const editorRoot = state.quill.root;
            const imgs = editorRoot.querySelectorAll('img[src="' + result.url + '"]:not([alt])');
            imgs.forEach(img => img.setAttribute('alt', altText));
          });
        }
        state.quill.setSelection(range.index + 1);
        updateChecklist();
      } catch (err) {
        toast('Image upload failed: ' + friendlyError(err), 'error');
      }
    };
  }

  function bindEditorEvents() {
    // Topic dropdown: reveal the free-text input when "+ New topic…" is picked
    const topicSel = document.getElementById('ag-pe-topic');
    if (topicSel) {
      topicSel.addEventListener('change', () => {
        const newInput = document.getElementById('ag-pe-topic-new');
        const isNew = topicSel.value === '__new__';
        newInput.style.display = isNew ? 'block' : 'none';
        if (isNew) newInput.focus();
      });
    }

    // Tag chip input
    const tagInput = document.getElementById('ag-pe-tags-input');
    tagInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        addTag(tagInput.value.trim());
        tagInput.value = '';
      } else if (e.key === 'Backspace' && tagInput.value === '') {
        const chips = document.querySelectorAll('#ag-pe-tags .ag-pe-chip');
        if (chips.length) chips[chips.length - 1].remove();
      }
    });
    tagInput.addEventListener('blur', () => {
      if (tagInput.value.trim()) {
        addTag(tagInput.value.trim());
        tagInput.value = '';
      }
    });

    // Author avatar upload
    document.getElementById('ag-pe-avatar-pick').addEventListener('click', () => {
      document.getElementById('ag-pe-avatar-input').click();
    });
    // Guarded: a stale-cache scenario (new JS, old HTML) would null this
    // and break the rest of the binding chain (including Save). Skip if
    // the button isn't in the DOM rather than throwing.
    const chooseAvatarBtn = document.getElementById('ag-pe-avatar-choose');
    if (chooseAvatarBtn) chooseAvatarBtn.addEventListener('click', openAuthorPicker);
    document.getElementById('ag-pe-avatar-input').addEventListener('change', async (e) => {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      const btn = document.getElementById('ag-pe-avatar-pick');
      btn.disabled = true;
      btn.innerHTML = '<span class="ag-pe-loading"></span>Uploading…';
      try {
        const prepared = await prepareImage(file);
        const base64 = await readFileAsBase64(prepared);
        const result = await api('upload_image', {
          filename: file.name, mimeType: prepared.type || file.type, base64: base64, altText: ''
        });
        state.authorAvatarUrl = result.url;
        const preview = document.getElementById('ag-pe-avatar-preview');
        preview.src = result.url; preview.style.display = 'block';
        btn.textContent = 'Replace avatar';
      } catch (err) {
        toast('Avatar upload failed: ' + friendlyError(err), 'error');
      } finally {
        btn.disabled = false;
      }
    });

    // Image upload
    document.getElementById('ag-pe-image-pick').addEventListener('click', () => {
      document.getElementById('ag-pe-image-input').click();
    });
    document.getElementById('ag-pe-image-clear').addEventListener('click', () => {
      state.featuredImage = { url: '', altText: '' };
      document.getElementById('ag-pe-image-preview').style.display = 'none';
      document.getElementById('ag-pe-image-clear').style.display = 'none';
      document.getElementById('ag-pe-image-hint').style.display = 'block';
      document.getElementById('ag-pe-image-alt').value = '';
    });
    document.getElementById('ag-pe-image-input').addEventListener('change', async (e) => {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      const btn = document.getElementById('ag-pe-image-pick');
      btn.disabled = true;
      btn.innerHTML = '<span class="ag-pe-loading"></span>Uploading…';
      try {
        const prepared = await prepareImage(file);
        const base64 = await readFileAsBase64(prepared);
        const result = await api('upload_image', {
          filename: file.name,
          mimeType: prepared.type || file.type,
          base64: base64,
          altText: document.getElementById('ag-pe-image-alt').value || ''
        });
        state.featuredImage.url = result.url;
        showFeaturedImage(result.url);
      } catch (err) {
        toast('Image upload failed: ' + friendlyError(err), 'error');
      } finally {
        btn.disabled = false;
        btn.textContent = 'Replace image';
      }
    });

    // Product search. Uses Shopify's storefront predictive search endpoint
    // (no Apps Script round-trip → ~100ms vs ~2s)
    const prodSearch = document.getElementById('ag-pe-prod-search');
    const prodResults = document.getElementById('ag-pe-prod-results');
    let prodDebounce;
    prodSearch.addEventListener('focus', async () => {
      if (!prodSearch.value.trim()) {
        // Show some defaults so the picker isn't empty
        try {
          const defaults = await fetchStorefrontProducts('', 8);
          if (defaults.length) renderProductResults(defaults);
        } catch (e) { /* non-fatal */ }
      }
    });
    prodSearch.addEventListener('input', () => {
      clearTimeout(prodDebounce);
      const q = prodSearch.value.trim();
      prodDebounce = setTimeout(async () => {
        try {
          const products = await fetchStorefrontProducts(q, 10);
          renderProductResults(products);
        } catch (err) {
          prodResults.innerHTML = `<div class="ag-pe-prod-result">${escapeHtml(err.message)}</div>`;
          prodResults.style.display = 'block';
        }
      }, 150);
    });
    document.addEventListener('click', (e) => {
      if (!prodResults.contains(e.target) && e.target !== prodSearch) {
        prodResults.style.display = 'none';
      }
    });

    // Save / cancel
    document.getElementById('ag-pe-save').addEventListener('click', () => save());
    document.getElementById('ag-pe-save-draft').addEventListener('click', () => save({ forceDraft: true }));
    document.getElementById('ag-pe-status').addEventListener('change', updateScheduleVisibility);
    document.getElementById('ag-pe-cancel').addEventListener('click', () => {
      if (confirm('Discard changes?')) goList();
    });
    // Fullscreen-only mirrored buttons (visible only when expanded)
    const fsSave = document.getElementById('ag-pe-fs-save');
    if (fsSave) fsSave.addEventListener('click', () => save());
    const fsSaveDraft = document.getElementById('ag-pe-fs-save-draft');
    if (fsSaveDraft) fsSaveDraft.addEventListener('click', () => save({ forceDraft: true }));

    // Populate the quick-links sidebar card + warm the authors cache
    // in the background so the picker opens instantly on first click.
    renderQuickLinks();
    loadQuickLinksFromBackend(); // override with backend-stored value if set
    // Fullscreen body editor toggle
    const fsBtn = document.getElementById('ag-pe-fullscreen');
    if (fsBtn) fsBtn.addEventListener('click', toggleEditorFullscreen);
    // Esc closes fullscreen
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const f = document.getElementById('ag-pe-body-field');
        if (f && f.classList.contains('is-fullscreen')) toggleEditorFullscreen();
      }
    });
    if (!_authorsCache) {
      api('list_authors', {}).then(r => { _authorsCache = r.authors || []; }).catch(() => {});
    }

    // Move post (edit mode only)
    const moveBtn = document.getElementById('ag-pe-move');
    if (moveBtn) {
      moveBtn.addEventListener('click', moveToBlog);
    }

    // Push to partner store (edit mode only)
    const bridgeBtn = document.getElementById('ag-pe-bridge');
    if (bridgeBtn) {
      bridgeBtn.addEventListener('click', pushToBridge);
    }

    // Auto-slug from title
    document.getElementById('ag-pe-title').addEventListener('blur', () => {
      const handleField = document.getElementById('ag-pe-handle');
      if (!handleField.value) {
        handleField.placeholder = slugify(document.getElementById('ag-pe-title').value);
      }
    });

    // Live SEO checklist. Recompute on any input change
    const watchSel = '#ag-pe-title, #ag-pe-summary, #ag-pe-seo-title, #ag-pe-seo-desc, #ag-pe-image-alt, #ag-pe-takeaways, #ag-pe-author-bio';
    document.querySelectorAll(watchSel).forEach(el => {
      el.addEventListener('input', updateChecklist);
    });
    if (state.quill) state.quill.on('text-change', updateChecklist);
    updateChecklist();
  }

  // ── SEO checklist ──────────────────────────────────────
  function updateChecklist() {
    const out = document.getElementById('ag-pe-checklist');
    if (!out) return;
    const title = document.getElementById('ag-pe-title').value || '';
    const summary = document.getElementById('ag-pe-summary').value || '';
    const seoTitle = document.getElementById('ag-pe-seo-title').value || '';
    const seoDesc  = document.getElementById('ag-pe-seo-desc').value || '';
    const imageAlt = document.getElementById('ag-pe-image-alt').value || '';
    const takeaways= document.getElementById('ag-pe-takeaways').value || '';
    const authorBio= document.getElementById('ag-pe-author-bio').value || '';

    const bodyHtml = state.quill ? state.quill.root.innerHTML : '';
    const bodyText = (state.quill ? state.quill.getText() : '').trim();
    const words = bodyText ? bodyText.split(/\s+/).length : 0;

    // Inline images in the body
    const imgs = (bodyHtml.match(/<img\b[^>]*>/gi) || []);
    const imgsMissingAlt = imgs.filter(t => !/\balt\s*=\s*"[^"]/i.test(t)).length;

    // Internal links. Relative paths or assassingoods.com
    const internalLinkPattern = /<a\b[^>]*href\s*=\s*"(?:\/|https?:\/\/[^"]*assassingoods\.com)/gi;
    const internalLinks = (bodyHtml.match(internalLinkPattern) || []).length;

    // Subheadings (H2/H3)
    const h2h3Count = (bodyHtml.match(/<h[23]\b/gi) || []).length;

    const items = [
      { ok: title.length > 0 && title.length <= 70, label: 'Title set (≤ 70 chars)', detail: title.length + ' chars' },
      { ok: summary.length >= 80, label: 'Excerpt ≥ 80 chars (lede + Google snippet)', detail: summary.length + ' chars' },
      { ok: !!state.featuredImage.url, label: 'Featured image uploaded' },
      { ok: imageAlt.trim().length > 0, label: 'Featured image alt text set' },
      { ok: words >= 300, label: 'Body ≥ 300 words', detail: words + ' words' },
      { ok: h2h3Count >= 2, label: 'At least 2 subheadings (H2/H3)', detail: h2h3Count },
      { ok: imgs.length === 0 || imgsMissingAlt === 0, label: 'All inline images have alt text', detail: imgs.length ? (imgsMissingAlt + ' missing') : 'no inline images' },
      { ok: internalLinks >= 1, label: 'At least 1 internal link', detail: internalLinks + ' link(s)' },
      { ok: takeaways.trim().length > 0, label: 'Key takeaways filled in' },
      { ok: seoTitle.length >= 40 && seoTitle.length <= 65, label: 'Meta title 40–65 chars', detail: seoTitle.length + (seoTitle ? ' chars' : ' (using post title)') },
      { ok: seoDesc.length >= 120 && seoDesc.length <= 165, label: 'Meta description 120–165 chars', detail: seoDesc.length + ' chars' },
      { ok: getTags().length >= 3, label: '≥ 3 tags', detail: getTags().length + ' tag(s)' },
      { ok: authorBio.trim().length > 0, label: 'Author bio set (E-E-A-T)' }
    ];

    out.innerHTML = items.map(i => `
      <li class="ag-pe-checklist__item ${i.ok ? 'is-ok' : 'is-todo'}">
        <span class="ag-pe-checklist__mark">${i.ok ? '✓' : '○'}</span>
        <span class="ag-pe-checklist__label">${escapeHtml(i.label)}${i.detail != null ? ` <small>${escapeHtml(String(i.detail))}</small>` : ''}</span>
      </li>
    `).join('');
  }

  function renderProductResults(products) {
    const wrap = document.getElementById('ag-pe-prod-results');
    if (!products.length) {
      wrap.innerHTML = `<div class="ag-pe-prod-result">No matches.</div>`;
      wrap.style.display = 'block';
      return;
    }
    wrap.innerHTML = products.map(p => `
      <div class="ag-pe-prod-result" data-product-id="${escapeAttr(p.id)}">
        <img src="${escapeAttr(p.imageUrl || '')}" alt="" onerror="this.style.visibility='hidden'">
        <span>${escapeHtml(p.title)}</span>
      </div>
    `).join('');
    wrap.style.display = 'block';
    wrap.querySelectorAll('.ag-pe-prod-result[data-product-id]').forEach(el => {
      el.addEventListener('click', () => {
        const id = el.getAttribute('data-product-id');
        const p = products.find(x => x.id === id);
        if (p) addProduct(p);
        wrap.style.display = 'none';
        document.getElementById('ag-pe-prod-search').value = '';
      });
    });
  }

  function addProduct(p) {
    if (state.selectedProducts.find(x => x.id === p.id)) return;
    state.selectedProducts.push(p);
    renderSelectedProducts();
  }

  function removeProduct(id) {
    state.selectedProducts = state.selectedProducts.filter(p => p.id !== id);
    renderSelectedProducts();
  }

  function renderSelectedProducts() {
    const wrap = document.getElementById('ag-pe-prod-selected');
    wrap.innerHTML = state.selectedProducts.map(p => `
      <div class="ag-pe-prod-row">
        <img src="${escapeAttr(p.imageUrl || '')}" alt="" onerror="this.style.visibility='hidden'">
        <span class="ag-pe-prod-row__title">${escapeHtml(p.title)}</span>
        <button type="button" class="ag-pe-btn ag-pe-btn--ghost ag-pe-btn--small" data-remove="${escapeAttr(p.id)}">Remove</button>
      </div>
    `).join('');
    wrap.querySelectorAll('[data-remove]').forEach(b => {
      b.addEventListener('click', () => removeProduct(b.getAttribute('data-remove')));
    });
  }

  // Tags
  function addTag(text) {
    if (!text) return;
    const wrap = document.getElementById('ag-pe-tags');
    const exists = Array.from(wrap.querySelectorAll('.ag-pe-chip')).some(
      c => c.getAttribute('data-tag').toLowerCase() === text.toLowerCase()
    );
    if (exists) return;
    const chip = document.createElement('span');
    chip.className = 'ag-pe-chip';
    chip.setAttribute('data-tag', text);
    chip.innerHTML = `${escapeHtml(text)}<button type="button" class="ag-pe-chip__remove" aria-label="Remove">&times;</button>`;
    chip.querySelector('.ag-pe-chip__remove').addEventListener('click', () => chip.remove());
    wrap.insertBefore(chip, document.getElementById('ag-pe-tags-input'));
  }

  function getTags() {
    return Array.from(document.querySelectorAll('#ag-pe-tags .ag-pe-chip'))
      .map(c => c.getAttribute('data-tag'));
  }

  // ── Topic picker ───────────────────────────────────────────────
  // The Topic dropdown owns the single `topic:` tag (what drives the
  // blog filter pills). It's kept OUT of the regular tag chips so it
  // isn't edited in two places. Source list comes from the section's
  // "Topics" setting (cfg.topics): one per line, optionally "Group | Topic".
  function parseTopicConfig() {
    const groups = {};   // ordered map: group label -> [topics]
    const flat = [];
    (cfg.topics || '').split('\n').forEach((line) => {
      line = line.trim();
      if (!line) return;
      let group = '', topic = line;
      const bar = line.indexOf('|');
      if (bar !== -1) { group = line.slice(0, bar).trim(); topic = line.slice(bar + 1).trim(); }
      if (!topic) return;
      if (group) { (groups[group] = groups[group] || []).push(topic); }
      else { flat.push(topic); }
    });
    return { groups, flat };
  }

  function renderTopicOptions() {
    const { groups, flat } = parseTopicConfig();
    const opt = (t) => `<option value="${escapeAttr('topic:' + t)}">${escapeHtml(t)}</option>`;
    let html = flat.map(opt).join('');
    Object.keys(groups).forEach((g) => {
      html += `<optgroup label="${escapeAttr(g)}">${groups[g].map(opt).join('')}</optgroup>`;
    });
    return html;
  }

  // Returns the selected 'topic:Name' tag (or '' for none).
  function getSelectedTopicTag() {
    const sel = document.getElementById('ag-pe-topic');
    if (!sel) return '';
    if (sel.value === '__new__') {
      const v = (document.getElementById('ag-pe-topic-new').value || '').trim().replace(/^topic:\s*/i, '');
      return v ? ('topic:' + v) : '';
    }
    return sel.value || '';
  }

  // Splits a tag list: routes the first topic: tag into the dropdown
  // (adding an option if it's not in the configured list) and returns
  // the remaining tags to render as normal chips. Any extra topic: tags
  // beyond the first are preserved as chips so nothing is lost.
  function setTopicFromTags(tags) {
    const sel = document.getElementById('ag-pe-topic');
    const topicTags = (tags || []).filter((t) => /^topic:/i.test(t));
    const rest = (tags || []).filter((t) => !/^topic:/i.test(t));
    if (sel && topicTags.length) {
      const tag = topicTags[0];
      const known = Array.from(sel.options).some((o) => o.value === tag);
      if (!known) {
        const o = document.createElement('option');
        o.value = tag;
        o.textContent = tag.replace(/^topic:/i, '');
        const newOpt = Array.from(sel.options).find((x) => x.value === '__new__');
        sel.insertBefore(o, newOpt || null);
      }
      sel.value = tag;
      return rest.concat(topicTags.slice(1));
    }
    return rest;
  }

  // ── Author picker modal ────────────────────────────────────────
  // Fetches the unique author roster from the backend (cached 5min
  // server-side) and renders a grid of avatars. Click an author →
  // populates the current post's author name, avatar URL, bio,
  // linkedin fields. Saves re-uploading the same avatar per post.
  let _authorsCache = null;
  let _authorPickerOpen = false;
  async function openAuthorPicker() {
    if (_authorPickerOpen) return; // ignore double-clicks while one is open
    _authorPickerOpen = true;

    // Loading overlay shown immediately so the user knows the click
    // landed. Replaced with the real picker once the fetch resolves.
    const loadingOverlay = document.createElement('div');
    loadingOverlay.className = 'ag-pe-author-overlay';
    loadingOverlay.innerHTML = `
      <div class="ag-pe-author-modal" role="dialog">
        <header class="ag-pe-author-modal__head">
          <h3>Loading authors…</h3>
          <button type="button" class="ag-pe-author-modal__close" aria-label="Close">×</button>
        </header>
        <p class="ag-pe-author-modal__lede" style="display:flex;align-items:center;gap:8px"><span class="ag-pe-loading"></span> Pulling the author roster from Shopify…</p>
      </div>
    `;
    function bail() { loadingOverlay.remove(); _authorPickerOpen = false; }
    document.body.appendChild(loadingOverlay);
    loadingOverlay.querySelector('.ag-pe-author-modal__close').addEventListener('click', bail);
    loadingOverlay.addEventListener('click', (e) => { if (e.target === loadingOverlay) bail(); });

    let authors;
    try {
      if (_authorsCache) {
        authors = _authorsCache;
      } else {
        const r = await api('list_authors', {});
        authors = r.authors || [];
        _authorsCache = authors;
      }
    } catch (err) {
      bail();
      return toast('Couldn\'t load authors, ' + friendlyError(err), 'error');
    }

    // Replace the loading overlay with the real picker
    loadingOverlay.remove();

    if (!authors.length) {
      _authorPickerOpen = false;
      return toast('No previous authors found. Upload an avatar to create the first one.', 'info');
    }

    const overlay = document.createElement('div');
    overlay.className = 'ag-pe-author-overlay';
    overlay.innerHTML = `
      <div class="ag-pe-author-modal" role="dialog" aria-labelledby="ag-pe-author-modal-title">
        <header class="ag-pe-author-modal__head">
          <h3 id="ag-pe-author-modal-title">Choose an author</h3>
          <button type="button" class="ag-pe-author-modal__close" aria-label="Close">×</button>
        </header>
        <p class="ag-pe-author-modal__lede">Click an author to fill the author name + avatar + bio + LinkedIn fields. You can still edit any of them after.</p>
        <div class="ag-pe-author-grid">
          ${authors.map((a, i) => `
            <button type="button" class="ag-pe-author-card" data-idx="${i}">
              ${a.avatar
                ? `<img class="ag-pe-author-card__img" src="${escapeAttr(a.avatar)}" alt="${escapeAttr(a.name)}" onerror="this.style.visibility='hidden'">`
                : `<span class="ag-pe-author-card__noimg">${escapeHtml(a.name.charAt(0).toUpperCase())}</span>`}
              <span class="ag-pe-author-card__name">${escapeHtml(a.name)}</span>
              <span class="ag-pe-author-card__usage">${a.usageCount} post${a.usageCount === 1 ? '' : 's'}</span>
            </button>
          `).join('')}
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    function close() { overlay.remove(); _authorPickerOpen = false; }
    overlay.querySelector('.ag-pe-author-modal__close').addEventListener('click', close);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

    overlay.querySelectorAll('.ag-pe-author-card').forEach(btn => {
      btn.addEventListener('click', () => {
        const a = authors[parseInt(btn.dataset.idx, 10)];
        document.getElementById('ag-pe-author').value = a.name || '';
        document.getElementById('ag-pe-author-bio').value = a.bio || '';
        document.getElementById('ag-pe-author-linkedin').value = a.linkedin || '';
        if (a.avatar) {
          state.authorAvatarUrl = a.avatar;
          const preview = document.getElementById('ag-pe-avatar-preview');
          preview.src = a.avatar; preview.style.display = 'block';
          document.getElementById('ag-pe-avatar-pick').textContent = 'Replace avatar';
        }
        close();
        toast('Author loaded, review + save', 'success');
      });
    });
  }

  // ── Link picker modal (products + collections) ─────────────────
  // Opens a searchable modal for inserting product or collection
  // links into the Quill body at the current cursor. If the user has
  // text selected, that text becomes the link text. Otherwise the
  // item's title is inserted as both text and href.
  async function openLinkPicker(kind) {
    const q = state.quill;
    if (!q) return;
    // Snapshot the selection NOW. Quill loses focus when the modal
    // opens, so we can't read it later.
    const savedRange = q.getSelection() || { index: q.getLength(), length: 0 };
    const action = kind === 'collection' ? 'list_collections' : 'list_products';
    const title = kind === 'collection' ? 'Insert collection link' : 'Insert product link';
    const placeholder = kind === 'collection' ? 'Search collections…' : 'Search products…';

    const overlay = document.createElement('div');
    overlay.className = 'ag-pe-link-overlay';
    overlay.innerHTML = `
      <div class="ag-pe-link-modal" role="dialog">
        <header class="ag-pe-link-modal__head">
          <h3>${title}</h3>
          <button type="button" class="ag-pe-link-modal__close" aria-label="Close">×</button>
        </header>
        <input type="search" class="ag-pe-link-modal__search" placeholder="${placeholder}" autofocus>
        <div class="ag-pe-link-modal__results" data-ag-link-results>
          <p class="ag-pe-link-modal__hint">${kind === 'collection' ? 'Loading collections…' : 'Type to search…'}</p>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);
    const input = overlay.querySelector('.ag-pe-link-modal__search');
    const results = overlay.querySelector('[data-ag-link-results]');

    function close() { overlay.remove(); }
    overlay.querySelector('.ag-pe-link-modal__close').addEventListener('click', close);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

    function insert(url, title) {
      const range = savedRange;
      if (range.length > 0) {
        q.format('link', url);
      } else {
        q.insertText(range.index, title, { link: url });
        q.setSelection(range.index + title.length, 0);
      }
      close();
    }

    function render(items) {
      if (!items.length) {
        results.innerHTML = `<p class="ag-pe-link-modal__hint">No matches. Try a different word.</p>`;
        return;
      }
      results.innerHTML = items.map((it, i) => `
        <button type="button" class="ag-pe-link-row" data-idx="${i}">
          ${it.imageUrl ? `<img class="ag-pe-link-row__img" src="${escapeAttr(it.imageUrl)}" alt="" onerror="this.style.visibility='hidden'">` : '<span class="ag-pe-link-row__img ag-pe-link-row__img--placeholder"></span>'}
          <span class="ag-pe-link-row__body">
            <span class="ag-pe-link-row__title">${escapeHtml(it.title)}</span>
            <span class="ag-pe-link-row__url">${escapeHtml((kind === 'collection' ? it.url : '/products/' + it.handle))}</span>
          </span>
        </button>
      `).join('');
      results.querySelectorAll('.ag-pe-link-row').forEach(btn => {
        btn.addEventListener('click', () => {
          const it = items[parseInt(btn.dataset.idx, 10)];
          const url = kind === 'collection' ? it.url : '/products/' + it.handle;
          insert(url, it.title);
        });
      });
    }

    async function fetchAndRender(query) {
      try {
        const payload = kind === 'collection' ? { query: query || '' } : { query: query || '' };
        const r = await api(action, payload);
        const items = (kind === 'collection' ? r.collections : r.products) || [];
        render(items);
      } catch (err) {
        results.innerHTML = `<p class="ag-pe-link-modal__hint">Couldn't load, ${escapeHtml(friendlyError(err))}</p>`;
      }
    }

    // Collections load immediately (no query needed); products only on type
    if (kind === 'collection') fetchAndRender('');

    let t;
    input.addEventListener('input', () => {
      clearTimeout(t);
      const v = input.value.trim();
      if (kind === 'product' && !v) {
        results.innerHTML = `<p class="ag-pe-link-modal__hint">Type to search…</p>`;
        return;
      }
      t = setTimeout(() => fetchAndRender(v), 220);
    });
  }

  // Quick Links bar in the fullscreen body editor. Editable via the
  // section's "Quick Links" textarea setting in the customizer:
  // one per line, format `Label | URL`. Parsed below; falls back to
  // a built-in default set if the setting is blank.
  const QUICK_LINKS_DEFAULT = [
    { label: 'Adjustable dumbbells', url: '/collections/adjustable-dumbbells-1' },
    { label: 'Kettlebells',          url: '/collections/kettlebells' },
    { label: 'All Things Stealth',   url: '/collections/all-things-stealth' },
    { label: 'AG Outlet',            url: '/pages/ag-outlet' },
    { label: 'Warranty',             url: '/pages/warranty' },
    { label: 'Returns',              url: '/policies/refund-policy' },
    { label: 'Finance, Payl8r',     url: '/pages/payl8r-finance-powered-by-dopple' },
    { label: 'Contact us',           url: '/pages/contact-us' }
  ];
  // Normalises a raw URL string:
  //   - Already has http/https → leave as-is
  //   - Relative path starting with `/` → leave as-is
  //   - Bare domain or domain+path (e.g. assassingoods.com, foo.com/bar)
  //     → prepend https://
  //   - Anchor or query-string only → leave as-is
  function normalizeUrl(url) {
    const u = String(url || '').trim();
    if (!u) return '';
    if (/^https?:\/\//i.test(u)) return u;
    if (u.charAt(0) === '/' || u.charAt(0) === '#' || u.charAt(0) === '?') return u;
    if (/^mailto:|^tel:/i.test(u)) return u;
    // Anything else with a dot before the first slash is likely a domain
    if (u.indexOf('.') !== -1) return 'https://' + u;
    return u; // last-resort: hand back unmodified
  }
  function parseQuickLinks(text) {
    if (!text || typeof text !== 'string') return QUICK_LINKS_DEFAULT;
    const lines = text.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
    const parsed = lines.map(line => {
      // Accept `Label | URL` (preferred) or `Label,URL` as a fallback.
      const sep = line.indexOf('|') !== -1 ? '|' : (line.indexOf(',') !== -1 ? ',' : null);
      if (!sep) return null;
      const idx = line.indexOf(sep);
      const label = line.slice(0, idx).trim();
      const url = normalizeUrl(line.slice(idx + 1));
      if (!label || !url) return null;
      return { label: label, url: url };
    }).filter(Boolean);
    return parsed.length ? parsed : QUICK_LINKS_DEFAULT;
  }
  // QUICK_LINKS is `let` because the inline editor mutates it after
  // the user saves changes via the front-end modal. The initial value
  // is parsed from the section setting (sensible defaults), then
  // replaced once the backend's stored value loads (overrides theme
  // setting if set).
  let QUICK_LINKS = parseQuickLinks((window.AG_POST_EDITOR_CONFIG || {}).quickLinks);
  let _quickLinksRawText = ''; // mirror of what's stored on the backend
  function renderQuickLinks() {
    const wrap = document.getElementById('ag-pe-quicklinks');
    if (!wrap) return;
    const chipsHtml = QUICK_LINKS.map((l, i) =>
      `<button type="button" class="ag-pe-quicklink" data-idx="${i}" title="Insert link to ${escapeAttr(l.url)}">${escapeHtml(l.label)}</button>`
    ).join('');
    wrap.innerHTML = chipsHtml +
      `<button type="button" class="ag-pe-quicklink ag-pe-quicklink--edit" data-ag-pe-qlinks-edit title="Edit quick links">⚙ Edit</button>`;
    wrap.querySelectorAll('.ag-pe-quicklink:not(.ag-pe-quicklink--edit)').forEach(btn => {
      btn.addEventListener('click', () => {
        const l = QUICK_LINKS[parseInt(btn.dataset.idx, 10)];
        insertQuickLink(l.url, l.label);
      });
    });
    wrap.querySelector('[data-ag-pe-qlinks-edit]').addEventListener('click', openQuickLinksEditor);
  }

  // Modal to edit the Quick Links inline. One textarea, format
  // `Label | URL` per line. Persists via the Apps Script
  // set_quick_links action, survives across browsers + sessions.
  function openQuickLinksEditor() {
    // Fall back to the current parsed list serialised back to text
    // if we haven't loaded the backend value yet.
    const seed = _quickLinksRawText || QUICK_LINKS.map(l => `${l.label} | ${l.url}`).join('\n');
    const overlay = document.createElement('div');
    overlay.className = 'ag-pe-author-overlay';
    overlay.innerHTML = `
      <div class="ag-pe-author-modal" role="dialog" style="max-width:560px">
        <header class="ag-pe-author-modal__head">
          <h3>Edit Quick Links</h3>
          <button type="button" class="ag-pe-author-modal__close" aria-label="Close">×</button>
        </header>
        <p class="ag-pe-author-modal__lede">One chip per line. Format: <code>Label | URL</code>. Leave blank to reset to defaults.</p>
        <textarea class="ag-pe-textarea" data-ag-pe-qlinks-text rows="12" style="margin:0 18px;width:calc(100% - 36px);font-family:'SF Mono',Menlo,monospace;font-size:13px"></textarea>
        <div style="display:flex;gap:8px;justify-content:flex-end;padding:14px 18px;border-top:1px solid var(--pe-border)">
          <button type="button" class="ag-pe-btn ag-pe-btn--ghost" data-ag-pe-qlinks-cancel>Cancel</button>
          <button type="button" class="ag-pe-btn" data-ag-pe-qlinks-save style="background:#0c0c0c;color:#fff;border:1px solid #0c0c0c;padding:8px 18px;font-weight:600;cursor:pointer;border-radius:4px">Save</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);
    const ta = overlay.querySelector('[data-ag-pe-qlinks-text]');
    ta.value = seed;
    ta.focus();

    function close() { overlay.remove(); }
    overlay.querySelector('.ag-pe-author-modal__close').addEventListener('click', close);
    overlay.querySelector('[data-ag-pe-qlinks-cancel]').addEventListener('click', close);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

    overlay.querySelector('[data-ag-pe-qlinks-save]').addEventListener('click', async () => {
      const text = ta.value;
      const btn = overlay.querySelector('[data-ag-pe-qlinks-save]');
      btn.disabled = true;
      btn.innerHTML = '<span class="ag-pe-loading"></span>Saving…';
      try {
        await api('set_quick_links', { text: text });
        _quickLinksRawText = text;
        QUICK_LINKS = parseQuickLinks(text);
        renderQuickLinks();
        toast('Quick links updated', 'success');
        close();
      } catch (err) {
        toast('Couldn\'t save, ' + friendlyError(err), 'error');
        btn.disabled = false;
        btn.textContent = 'Save';
      }
    });
  }

  // Loads the backend-stored Quick Links text on editor render so
  // it overrides the section-setting fallback. Silent on error.
  async function loadQuickLinksFromBackend() {
    try {
      const r = await api('get_quick_links', {});
      if (r && typeof r.text === 'string' && r.text.trim()) {
        _quickLinksRawText = r.text;
        QUICK_LINKS = parseQuickLinks(r.text);
        renderQuickLinks();
      }
    } catch (e) { /* silent, keeps section-setting / default fallback */ }
  }

  // Insert a static link at the LAST KNOWN Quill cursor position
  // (clicking the chip steals focus from Quill, so getSelection() at
  // click-time returns null, we use state.lastRange, updated on
  // every Quill selection-change while the editor had focus).
  function insertQuickLink(url, label) {
    const q = state.quill;
    if (!q) return toast('Quill not ready yet', 'error');
    const range = state.lastRange || { index: q.getLength(), length: 0 };
    if (range.length > 0) {
      // Wrap selection
      q.setSelection(range.index, range.length);
      q.format('link', url);
    } else {
      q.insertText(range.index, label, { link: url });
      q.setSelection(range.index + label.length, 0);
    }
    // Update tracked range so consecutive chip clicks insert sequentially
    state.lastRange = { index: range.index + (range.length || label.length), length: 0 };
    toast('Link inserted', 'success');
  }

  // Toggle the datetime input based on the status select
  function updateScheduleVisibility() {
    const status = document.getElementById('ag-pe-status');
    const wrap = document.getElementById('ag-pe-schedule-wrap');
    if (!status || !wrap) return;
    wrap.style.display = status.value === 'schedule' ? '' : 'none';
  }

  // Featured image preview
  function showFeaturedImage(url) {
    const preview = document.getElementById('ag-pe-image-preview');
    preview.src = url; preview.style.display = 'block';
    document.getElementById('ag-pe-image-hint').style.display = 'none';
    document.getElementById('ag-pe-image-clear').style.display = 'inline-block';
    document.getElementById('ag-pe-image-pick').textContent = 'Replace image';
  }

  // Hydrate when editing
  async function hydrateForm(a) {
    document.getElementById('ag-pe-title').value = a.title || '';
    document.getElementById('ag-pe-summary').value = a.summary || '';
    {
      const status = articleStatus(a);
      const statusValue = status === 'scheduled' ? 'schedule' : (status === 'live' ? 'publish' : 'draft');
      document.getElementById('ag-pe-status').value = statusValue;
      if (status === 'scheduled' && a.publishedAt) {
        document.getElementById('ag-pe-schedule').value = isoToLocalDatetime(a.publishedAt);
      }
      updateScheduleVisibility();
    }
    document.getElementById('ag-pe-blog').value = a.blogId || '';
    document.getElementById('ag-pe-author').value = a.authorName || '';
    document.getElementById('ag-pe-handle').value = a.handle || '';
    document.getElementById('ag-pe-seo-title').value = a.seoTitle || '';
    document.getElementById('ag-pe-seo-desc').value = a.seoDescription || '';
    document.getElementById('ag-pe-image-alt').value = a.imageAltText || '';
    document.getElementById('ag-pe-post-type').value = a.postType || 'general';
    document.getElementById('ag-pe-takeaways').value = a.keyTakeaways || '';
    document.getElementById('ag-pe-author-bio').value = a.authorBio || '';
    document.getElementById('ag-pe-author-linkedin').value = a.authorLinkedin || '';
    if (a.authorAvatar) {
      state.authorAvatarUrl = a.authorAvatar;
      const preview = document.getElementById('ag-pe-avatar-preview');
      preview.src = a.authorAvatar; preview.style.display = 'block';
      document.getElementById('ag-pe-avatar-pick').textContent = 'Replace avatar';
    }
    if (a.imageUrl) {
      state.featuredImage.url = a.imageUrl;
      state.featuredImage.altText = a.imageAltText || '';
      showFeaturedImage(a.imageUrl);
    }
    setTopicFromTags(a.tags || []).forEach(addTag);

    // Related products: fetch details by GID
    if (a.relatedProductGids && a.relatedProductGids.length) {
      try {
        const data = await api('list_products', { ids: a.relatedProductGids });
        state.selectedProducts = data.products || [];
        renderSelectedProducts();
      } catch (e) { /* non-fatal */ }
    }
    updateChecklist();
  }

  // ── Move post to another blog ───────────────────────────
  async function moveToBlog() {
    const select = document.getElementById('ag-pe-move-target');
    const targetId = select.value;
    if (!targetId) {
      toast('Pick a blog to move to', 'error');
      return;
    }
    const targetTitle = select.options[select.selectedIndex].text;
    const ok = confirm(
      'Move this post to "' + targetTitle + '"?\n\n' +
      'This will recreate the post in the new blog and delete the original. ' +
      'The URL will change. The old URL will 404 (Shopify does not auto-redirect).\n\n' +
      'This cannot be undone.'
    );
    if (!ok) return;

    const btn = document.getElementById('ag-pe-move');
    btn.disabled = true;
    btn.innerHTML = '<span class="ag-pe-loading"></span>Moving…';
    try {
      const result = await api('move_article', {
        article_id: state.currentArticle.id,
        target_blog_id: targetId
      }, 60000);
      toast('Moved, loading new post…', 'success');
      // Load the new article (which is now in the target blog)
      setTimeout(() => goEditor(result.article.id), 1000);
    } catch (err) {
      toast('Move failed, ' + friendlyError(err), 'error');
      btn.disabled = false;
      btn.textContent = 'Move';
    }
  }

  // ── Cross-store bridge ──────────────────────────────────
  // Sends the current post to the partner store (other language /
  // brand). The partner backend machine-translates it and creates a
  // DRAFT for review, nothing is published.
  async function pushToBridge() {
    if (!state.currentArticle) return;
    const label = bridgeLabel || 'the partner store';
    const ok = confirm(
      label + '?\n\n' +
      'This sends the post to the other store, where it is machine-translated ' +
      'and created as a DRAFT for review. Nothing is published.\n\n' +
      '• Related products are NOT carried over (different catalog).\n' +
      '• Collection / product links may need re-pointing.\n' +
      '• Machine translation needs a human proofread before publishing.'
    );
    if (!ok) return;

    const btn = document.getElementById('ag-pe-bridge');
    const original = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.innerHTML = '<span class="ag-pe-loading"></span>Sending…'; }
    try {
      // Generous timeout, the partner translates field-by-field server-side.
      const result = await api('bridge_push', { article_id: state.currentArticle.id }, 180000);
      let msg = 'Sent to ' + (result.targetStore || 'the partner store') + ' as a draft.';
      if (result.warning) msg += ' ⚠ ' + result.warning;
      toast(msg, result.warning ? 'error' : 'success');
      if (result.adminUrl) window.open(result.adminUrl, '_blank');
    } catch (err) {
      toast('Push failed, ' + friendlyError(err), 'error');
    } finally {
      if (btn) { btn.disabled = false; btn.textContent = original || bridgeLabel; }
    }
  }

  // ── Save ────────────────────────────────────────────────
  async function save(opts) {
    const forceDraft = !!(opts && opts.forceDraft);
    const data = {
      title: document.getElementById('ag-pe-title').value.trim(),
      summary: document.getElementById('ag-pe-summary').value.trim(),
      body: state.quill ? state.quill.root.innerHTML : '',
      blogId: document.getElementById('ag-pe-blog').value,
      authorName: document.getElementById('ag-pe-author').value.trim(),
      handle: document.getElementById('ag-pe-handle').value.trim(),
      ...(() => {
        if (forceDraft) return { isPublished: false, publishedAt: null };
        const status = document.getElementById('ag-pe-status').value;
        const scheduleVal = document.getElementById('ag-pe-schedule').value;
        if (status === 'draft') return { isPublished: false, publishedAt: null };
        if (status === 'schedule' && scheduleVal) {
          // Shopify GraphQL: setting isPublished: true with a future
          // publishedAt publishes NOW (the publishedAt is just metadata).
          // For an article to be SCHEDULED (hidden until the date),
          // it must be created with isPublished: false + future
          // publishedAt. Shopify's admin then shows "Scheduled" and
          // (in admin UI) flips isPublished automatically on the date.
          return { isPublished: false, publishedAt: localDatetimeToIso(scheduleVal) };
        }
        return { isPublished: true, publishedAt: new Date().toISOString() };
      })(),
      tags: (function () {
        var t = getTags();
        var topic = getSelectedTopicTag();
        if (topic && !t.some(function (x) { return x.toLowerCase() === topic.toLowerCase(); })) t = t.concat([topic]);
        return t;
      })(),
      seoTitle: document.getElementById('ag-pe-seo-title').value.trim(),
      seoDescription: document.getElementById('ag-pe-seo-desc').value.trim(),
      imageUrl: state.featuredImage.url || '',
      imageAltText: document.getElementById('ag-pe-image-alt').value.trim(),
      postType: document.getElementById('ag-pe-post-type').value || 'general',
      keyTakeaways: document.getElementById('ag-pe-takeaways').value.trim(),
      authorBio: document.getElementById('ag-pe-author-bio').value.trim(),
      authorAvatar: state.authorAvatarUrl || '',
      authorLinkedin: document.getElementById('ag-pe-author-linkedin').value.trim(),
      relatedProductGids: state.selectedProducts.map(p => p.id)
    };

    if (!data.title) return toast('Title required', 'error');
    if (!data.blogId) return toast('Pick a blog', 'error');
    if (!forceDraft
        && document.getElementById('ag-pe-status').value === 'schedule'
        && !document.getElementById('ag-pe-schedule').value) {
      return toast('Pick a date + time to schedule', 'error');
    }

    const btn = document.getElementById('ag-pe-save');
    const savedLabel = state.currentArticle ? 'Save changes' : 'Create post';
    btn.disabled = true;
    btn.innerHTML = '<span class="ag-pe-loading"></span>Saving…';
    try {
      if (state.currentArticle) {
        await api('update_article', { article_id: state.currentArticle.id, data: data });
        toast('Saved', 'success');
        btn.textContent = 'Save changes';
      } else {
        const result = await api('create_article', { data: data });
        // Convert this session to "edit" mode so subsequent saves update
        // the same post rather than creating duplicates.
        state.currentArticle = {
          id: result.article.id,
          handle: result.article.handle || '',
          blogHandle: result.article.blogHandle || '',
          // Carry the just-submitted data so any in-editor render uses it,
          // not a stale read while Shopify finishes propagating metafields.
          title: data.title,
          summary: data.summary,
          body: data.body,
          tags: data.tags,
          isPublished: data.isPublished,
          authorName: data.authorName,
          imageUrl: data.imageUrl,
          imageAltText: data.imageAltText,
          seoTitle: data.seoTitle,
          seoDescription: data.seoDescription,
          postType: data.postType,
          keyTakeaways: data.keyTakeaways,
          authorBio: data.authorBio,
          authorAvatar: data.authorAvatar,
          authorLinkedin: data.authorLinkedin,
          relatedProductGids: data.relatedProductGids
        };
        toast('Created, saved. Click "← All posts" when done.', 'success');
        btn.textContent = 'Save changes';
      }
      // Editor stays put on save. Form retains what you just submitted,
      // no race with Shopify's metafield propagation. Tab back to list
      // when you're ready.
    } catch (err) {
      toast(friendlyError(err), 'error');
    } finally {
      btn.disabled = false;
      // Always restore the button label so "Saving…" doesn't get stuck on
      // error or timeout. After a successful save the success branch may
      // have already set it to "Save changes", overwrite either way.
      btn.textContent = state.currentArticle ? 'Save changes' : savedLabel;
    }
  }

  // ── Tab routing ─────────────────────────────────────────
  function bindTabs() {
    root.querySelectorAll('.ag-pe-tab').forEach(t => {
      t.addEventListener('click', () => {
        const which = t.getAttribute('data-tab');
        if (which === 'list') goList();
        else if (which === 'new') goEditor(null);
        else if (which === 'info') goInfo();
      });
    });
  }

  // ── Help / how-it-works view ────────────────────────────
  function goInfo() {
    state.view = 'info';
    root.innerHTML = `
      <div class="ag-pe-tabs">
        <button class="ag-pe-tab" data-tab="list">Posts</button>
        <button class="ag-pe-tab" data-tab="new">+ New post</button>
        <button class="ag-pe-tab is-active" data-tab="info">How it works</button>
      </div>
      <div style="max-width:820px;margin:14px auto 60px;padding:0 4px;font-size:14px;line-height:1.55;color:#1a1a1a;">
        <h3 style="font-size:18px;margin:0 0 8px;">What this is</h3>
        <p>Staff blog editor for the AG site. Write a post, attach images, schedule it, hit publish. It goes through Shopify's Blog API to the live blog. No need to use Shopify admin's blog editor (which is clunky and doesn't handle our custom article fields).</p>

        <h3 style="font-size:18px;margin:22px 0 8px;">Posts tab. The list view</h3>
        <ul>
          <li>Lists every existing post across every AG blog (filter by blog or search by title).</li>
          <li>Click a post to edit. Includes draft + scheduled posts (those have a status pill).</li>
          <li>The order is "most recently edited"your in-progress drafts always float to the top.</li>
        </ul>

        <h3 style="font-size:18px;margin:22px 0 8px;">+ New post tab. The editor</h3>
        <p>Quill v2 rich-text editor. Top toolbar controls work like any word-processor; the lesser-known things:</p>
        <ul>
          <li><strong>Tables</strong>: toolbar has a "table" button. Click to insert; right-click an inserted table for row/col controls.</li>
          <li><strong>Images</strong>: drag onto the canvas OR use the image toolbar button. Images upload to Drive immediately and the public URL is inserted. Don't paste base64. Quill accepts it but the file size will balloon the post.</li>
          <li><strong>Product cards / auto-link</strong>: type a product handle in <code>[brackets]</code> and a product card preview shows up at preview time. AG blog post system auto-links matching product names too, so don't bother manually linking every "Black Edition" mention.</li>
          <li><strong>Drafts</strong>: every keystroke is auto-saved as a draft to the Apps Script backend, so closing the tab won't lose work.</li>
        </ul>

        <h3 style="font-size:18px;margin:22px 0 8px;">Sidebar fields explained</h3>
        <dl>
          <dt><strong>Blog</strong></dt><dd>Which AG blog this post belongs to (Stealth Build, Inside the Build, Squat Cradle, etc.). Determines the URL prefix.</dd>
          <dt><strong>Status, Draft / Scheduled / Published</strong></dt><dd>Draft = invisible to customers; Scheduled = will auto-publish on the chosen date; Published = live now.</dd>
          <dt><strong>Tags</strong></dt><dd>Drives the related-posts module on each article page. Tag consistently, "kettlebells", "dumbbells", "training" are the buckets we use.</dd>
          <dt><strong>Excerpt</strong></dt><dd>The summary shown on the blog index card + on social shares. Keep under 160 chars.</dd>
          <dt><strong>Featured image</strong></dt><dd>Shows on the blog index card + at the top of the article. 16:9 aspect.</dd>
          <dt><strong>Author</strong></dt><dd>Shows under the title. Pick from the dropdown. We don't free-type names so the author bio module renders properly.</dd>
          <dt><strong>SEO title / meta description</strong></dt><dd>Optional, defaults to the post title + excerpt if blank. Override only if you need a longer title for search but a snappier one for the card.</dd>
        </dl>

        <h3 style="font-size:18px;margin:22px 0 8px;">Image uploads</h3>
        <p>Images go to a shared Google Drive folder via the Apps Script backend. Drag-and-drop in the editor or use the toolbar button. The Drive URL is what's saved into the post. So if you delete the Drive file, the image breaks on the live post. Don't move files around in Drive.</p>

        <h3 style="font-size:18px;margin:22px 0 8px;">Troubleshooting</h3>
        <dl>
          <dt><strong>"Wrong password" error</strong></dt><dd>The backend has brute-force protection, 8 wrong tries → 15 min lockout. Wait it out or ask in the team chat.</dd>
          <dt><strong>Save fails partway through</strong></dt><dd>Usually a Shopify Admin API throttle. The Quill canvas keeps your local copy. Just retry the save.</dd>
          <dt><strong>Image won't upload</strong></dt><dd>File size cap is ~25 MB. Resize first if it's a phone photo, JPEG, max 2000px wide.</dd>
          <dt><strong>Post published but looks wrong on the live site</strong></dt><dd>Hard-refresh. Shopify caches blog content for ~60 seconds. If layout is genuinely broken (tables especially), check the HTML view in Quill (it's under the "&lt;&gt;" toolbar button) for stray Quill artefacts.</dd>
          <dt><strong>Scheduled post didn't publish on time</strong></dt><dd>Shopify's scheduler runs every ~15 min. If it's been over an hour, check the post status, sometimes the schedule reverts to Draft if Shopify rejected the publish.</dd>
        </dl>

        <h3 style="font-size:18px;margin:22px 0 8px;">Where data lives</h3>
        <p>Backend is the <code>ag-post-editor-script</code> Apps Script project. Posts themselves live in Shopify (this just talks to Shopify's Blog API). Images live in a Drive folder. Drafts/working state live in Script Properties.</p>
      </div>
    `;
    bindTabs();
  }

  // ── Storefront product search (fast, no Apps Script) ────
  async function fetchStorefrontProducts(query, limit) {
    if (query) {
      const url = '/search/suggest.json?q=' + encodeURIComponent(query)
        + '&resources[type]=product'
        + '&resources[limit]=' + (limit || 10)
        + '&resources[options][fields]=title,product_type,tag';
      const resp = await fetch(url, { headers: { 'Accept': 'application/json' } });
      const data = await resp.json();
      const list = (data && data.resources && data.resources.results && data.resources.results.products) || [];
      return list.map(p => ({
        id: 'gid://shopify/Product/' + p.id,
        title: p.title,
        imageUrl: p.image || '',
        handle: p.handle || ''
      }));
    }
    // No query, fetch most recent products as defaults
    const resp = await fetch('/products.json?limit=' + (limit || 8));
    const data = await resp.json();
    return (data.products || []).map(p => ({
      id: 'gid://shopify/Product/' + p.id,
      title: p.title,
      imageUrl: (p.images && p.images[0] && p.images[0].src) || '',
      handle: p.handle || ''
    }));
  }

  // ── Utilities ───────────────────────────────────────────
  function readFileAsBase64(file) {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => {
        const result = r.result || '';
        const base64 = String(result).split(',')[1] || '';
        resolve(base64);
      };
      r.onerror = reject;
      r.readAsDataURL(file);
    });
  }

  // Shrinks image client-side before upload, typical phone photo
  // goes from ~5MB to ~300KB at 1600px / quality 0.85.
  // Returns a Blob (still .name / .type / .size compatible enough for our
  // upload path). Falls through to original file for SVG and small images.
  async function prepareImage(file) {
    const MAX_DIM = 1600;
    const QUALITY = 0.85;
    if (!file || !file.type || !file.type.startsWith('image/')) return file;
    if (file.type === 'image/svg+xml' || file.type === 'image/gif') return file;
    try {
      const bitmap = await createImageBitmap(file);
      const { width, height } = bitmap;
      const maxSide = Math.max(width, height);
      if (maxSide <= MAX_DIM && file.size < 800 * 1024) {
        bitmap.close && bitmap.close();
        return file;
      }
      const scale = maxSide > MAX_DIM ? MAX_DIM / maxSide : 1;
      const w = Math.round(width * scale);
      const h = Math.round(height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(bitmap, 0, 0, w, h);
      bitmap.close && bitmap.close();
      const targetType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
      return await new Promise((resolve) =>
        canvas.toBlob((blob) => {
          if (!blob) return resolve(file);
          // Preserve filename so the server-side has something sensible
          blob.name = file.name;
          resolve(blob);
        }, targetType, QUALITY)
      );
    } catch (e) {
      return file;
    }
  }
  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function escapeAttr(s) { return escapeHtml(s); }
  function formatDate(iso) {
    if (!iso) return 'Unpublished';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch (e) { return ''; }
  }
  function slugify(s) {
    return String(s || '').toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-').replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }
  function articleStatus(a) {
    if (!a || !a.isPublished) return 'draft';
    if (a.publishedAt && new Date(a.publishedAt).getTime() > Date.now()) return 'scheduled';
    return 'live';
  }
  function articleStatusLabel(s) {
    return s === 'scheduled' ? 'Scheduled' : (s === 'live' ? 'Live' : 'Draft');
  }
  // datetime-local input value (e.g. "2026-06-01T14:30") → ISO with TZ
  function localDatetimeToIso(local) {
    if (!local) return '';
    try { return new Date(local).toISOString(); } catch (e) { return ''; }
  }
  // ISO → datetime-local format (the input expects yyyy-MM-ddTHH:mm in local time)
  function isoToLocalDatetime(iso) {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const pad = n => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch (e) { return ''; }
  }

  // ── Bootstrap ───────────────────────────────────────────
  if (!endpoint) {
    root.innerHTML = `<div class="ag-pe-error-banner">No backend endpoint configured. Set it in the theme editor (AG Post Editor section settings).</div>`;
    return;
  }
  function startApp() {
    api('list_blogs').then(data => {
      state.blogs = data.blogs || [];
      goList();
    }).catch(err => {
      if (window.AG_ADMIN && typeof window.AG_ADMIN.api === 'function') {
        // The shell owns auth; on an auth error it shows its own OTP overlay.
        // Surface anything else (e.g. section forbidden, backend down).
        const msg = String(err && err.message || err);
        if (msg === 'auth') return;
        root.innerHTML = `<div class="ag-pe-error-banner">Could not load the editor: ${escapeHtml(msg)}</div>`;
        return;
      }
      state.password = '';
      sessionStorage.removeItem('agpe_password');
      renderLogin();
    });
  }

  if (window.AG_ADMIN && typeof window.AG_ADMIN.onReady === 'function') {
    // OTP shell present: it owns auth. Wait for a valid session, then load.
    root.innerHTML = `<div class="ag-pe-login"><h2 class="ag-pe-login__title">Loading…</h2></div>`;
    window.AG_ADMIN.onReady(startApp);
  } else if (state.password) {
    startApp();
  } else {
    renderLogin();
  }
})();
